/**
 * File Upload Error Parser
 *
 * Parses API error responses and transforms them into user-friendly messages
 * with optional technical details for debugging.
 */

import i18next from "i18next";

export interface FileUploadError {
  /** User-friendly error message */
  userMessage: string;
  /** Technical details for debugging */
  technicalDetails: {
    errorCode?: string;
    status?: number;
    fileName?: string;
    fileSize?: string;
    originalMessage?: string;
    timestamp: string;
  };
  /** Helpful suggestions for the user */
  suggestions?: string[];
}

/**
 * Gets suggestions for structured backend error categories
 */
function getSuggestionsForCategory(category: string): string[] {
  const t = i18next.t;

  switch (category) {
    case "security":
      return [
        t("fileUpload.errors.security.suggestion1", {
          defaultValue: "Contact support if you believe this file is safe",
        }),
        t("fileUpload.errors.security.suggestion2", {
          defaultValue: "Try a different file if this one was corrupted",
        }),
      ];
    case "file_access":
      return [
        t("fileUpload.errors.fileAccess.suggestion1", {
          defaultValue: "Try uploading the file again",
        }),
        t("fileUpload.errors.fileAccess.suggestion2", {
          defaultValue: "Ensure the file still exists and is not corrupted",
        }),
      ];
    case "permissions":
      return [
        t("fileUpload.errors.permissions.suggestion1", {
          defaultValue: "Contact your administrator for file access permissions",
        }),
      ];
    case "timeout":
      return [
        t("fileUpload.errors.timeout.suggestion1", {
          defaultValue: "Try uploading a smaller file",
        }),
        t("fileUpload.errors.timeout.suggestion2", {
          defaultValue: "Check your network connection and try again",
        }),
      ];
    default:
      return [];
  }
}

/**
 * Error codes a retry cannot fix: the file itself is rejected (content, type, size, name clash,
 * quarantine) or the user lacks permission. Everything else, such as network, timeout and
 * unknown server failures, can succeed on a later attempt.
 */
const NON_RETRYABLE_ERROR_CODES: ReadonlySet<string> = new Set([
  "INVALID_FILE_CONTENT",
  "UNSUPPORTED_TYPE",
  "FILE_TOO_LARGE",
  "UNAUTHORIZED",
  "MALWARE_DETECTED",
  "DUPLICATE_FILE",
  "SECURITY_ERROR",
  "PERMISSIONS_ERROR",
]);

/** True when retrying the same upload could succeed, so Retry and the retry cap apply to it. */
export function isRetryableUploadErrorCode(errorCode: string | undefined): boolean {
  return !errorCode || !NON_RETRYABLE_ERROR_CODES.has(errorCode);
}

/**
 * Gets localized error message and suggestions for a given error code
 */
function getErrorMapping(errorCode: string): { message: string; suggestions?: string[] } {
  const t = i18next.t;

  switch (errorCode) {
    case "INVALID_FILE_CONTENT":
      return {
        message: t("fileUpload.errors.invalidContent.message", {
          defaultValue: "This file appears to be corrupted or has an incorrect extension.",
        }),
        suggestions: [
          t("fileUpload.errors.invalidContent.suggestion1", {
            defaultValue: "Try re-saving the file as a proper PDF or document",
          }),
          t("fileUpload.errors.invalidContent.suggestion2", {
            defaultValue: "Verify the file is not corrupted by opening it locally",
          }),
          t("fileUpload.errors.invalidContent.suggestion3", {
            defaultValue: "Check if the file was renamed with an incorrect extension",
          }),
        ],
      };

    case "UNSUPPORTED_TYPE":
      return {
        message: t("fileUpload.errors.unsupportedType.message", {
          defaultValue: "This file type is not supported.",
        }),
        suggestions: [
          t("fileUpload.errors.unsupportedType.suggestion1", {
            defaultValue:
              "Supported types: PDF, DOCX, PPTX, PNG, JPG, CSV, XLSX, TXT, MD, JSON, YAML, XML",
          }),
          t("fileUpload.errors.unsupportedType.suggestion2", {
            defaultValue: "Try converting your file to a supported format",
          }),
        ],
      };

    case "FILE_TOO_LARGE":
      return {
        message: t("fileUpload.errors.fileTooLarge.message", {
          defaultValue: "File exceeds the maximum size limit.",
        }),
        suggestions: [
          t("fileUpload.errors.fileTooLarge.suggestion1", {
            defaultValue: "Try compressing the file to reduce its size",
          }),
          t("fileUpload.errors.fileTooLarge.suggestion2", {
            defaultValue: "Split large documents into smaller parts",
          }),
        ],
      };

    case "NETWORK_ERROR":
      return {
        message: t("fileUpload.errors.networkError.message", {
          defaultValue: "Connection lost while uploading.",
        }),
        suggestions: [
          t("fileUpload.errors.networkError.suggestion1", {
            defaultValue: "Check your internet connection",
          }),
          t("fileUpload.errors.networkError.suggestion2", {
            defaultValue: "Try uploading again in a few moments",
          }),
        ],
      };

    case "TIMEOUT":
      return {
        message: t("fileUpload.errors.timeout.message", {
          defaultValue: "Upload timed out.",
        }),
        suggestions: [
          t("fileUpload.errors.timeout.suggestion1", {
            defaultValue: "The file may be too large or your connection too slow",
          }),
          t("fileUpload.errors.timeout.suggestion2", {
            defaultValue: "Try uploading a smaller file or retry with a better connection",
          }),
        ],
      };

    case "UNAUTHORIZED":
      return {
        message: t("fileUpload.errors.unauthorized.message", {
          defaultValue: "You don't have permission to upload files.",
        }),
        suggestions: [
          t("fileUpload.errors.unauthorized.suggestion1", {
            defaultValue: "Contact your administrator for file upload permissions",
          }),
          t("fileUpload.errors.unauthorized.suggestion2", {
            defaultValue: "Try logging out and logging back in",
          }),
        ],
      };

    case "MALWARE_DETECTED":
      return {
        message: t("fileUpload.errors.malwareDetected.message", {
          defaultValue:
            "This file has been identified as potentially harmful and has been quarantined.",
        }),
        suggestions: [
          t("fileUpload.errors.malwareDetected.suggestion1", {
            defaultValue: "Contact support if you believe this file is safe",
          }),
          t("fileUpload.errors.malwareDetected.suggestion2", {
            defaultValue: "Try uploading a different file",
          }),
        ],
      };

    case "DUPLICATE_FILE":
      return {
        message: t("fileUpload.errors.duplicateFile.message", {
          defaultValue: "Files with these names have already been added.",
        }),
        suggestions: [
          t("fileUpload.errors.duplicateFile.suggestion1", {
            defaultValue: "Remove the existing files first if you want to replace them",
          }),
          t("fileUpload.errors.duplicateFile.suggestion2", {
            defaultValue: "Rename the files before uploading",
          }),
        ],
      };

    default:
      return {
        message: t("fileUpload.errors.unknown.message", {
          defaultValue: "Upload failed. Please try again.",
        }),
        suggestions: [
          t("fileUpload.errors.unknown.suggestion1", {
            defaultValue: "If the problem persists, contact support",
          }),
        ],
      };
  }
}

/**
 * Formats file size in bytes to human-readable format
 */
function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
}

/**
 * Extracts specific error code from technical_details string
 * Handles formats like: "400: {'error': 'MALWARE_DETECTED', 'message': '...'}"
 */
function extractErrorCodeFromTechnicalDetails(technicalDetails: string): string | undefined {
  if (!technicalDetails || typeof technicalDetails !== "string") {
    return undefined;
  }

  // Match known error codes in the technical_details string
  const knownErrorCodes = [
    "MALWARE_DETECTED",
    "INVALID_FILE_CONTENT",
    "UNSUPPORTED_TYPE",
    "FILE_TOO_LARGE",
    "NETWORK_ERROR",
    "TIMEOUT",
    "UNAUTHORIZED",
    "DUPLICATE_FILE",
  ];

  for (const code of knownErrorCodes) {
    if (technicalDetails.includes(code)) {
      return code;
    }
  }

  // Try to extract from 'error': 'CODE' pattern
  const errorMatch = technicalDetails.match(/'error':\s*'([A-Z_]+)'/);

  if (errorMatch) {
    return errorMatch[1];
  }

  return undefined;
}

/**
 * Extracts error code from API response
 */
function extractErrorCode(error: any): string | undefined {
  // Try detail.error field (highest priority - from API)
  if (error?.detail?.error) {
    return error.detail.error;
  }

  // Try response.data patterns (API response)
  if (error?.response?.data?.detail?.error) {
    return error.response.data.detail.error;
  }

  // Try error message string matching (for cases where error is wrapped in message)
  if (error?.message && typeof error.message === "string") {
    const match = error.message.match(
      /INVALID_FILE_CONTENT|UNSUPPORTED_TYPE|FILE_TOO_LARGE|NETWORK_ERROR|TIMEOUT|UNAUTHORIZED|DUPLICATE_FILE/,
    );

    if (match) {
      return match[0];
    }
  }

  // Try detail string matching
  if (typeof error?.detail === "string") {
    const match = error.detail.match(
      /INVALID_FILE_CONTENT|UNSUPPORTED_TYPE|FILE_TOO_LARGE|DUPLICATE_FILE/,
    );

    if (match) {
      return match[0];
    }
  }

  // Try error code field (lower priority - may not be semantic)
  if (error?.code) {
    return error.code;
  }

  return undefined;
}

/**
 * Extracts error message from API response
 */
function extractErrorMessage(error: any): string | undefined {
  // Try detail.message field
  if (error?.detail?.message) {
    return error.detail.message;
  }

  // Try detail string
  if (typeof error?.detail === "string") {
    return error.detail;
  }

  // Try response.data patterns
  if (error?.response?.data?.detail?.message) {
    return error.response.data.detail.message;
  }

  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  // Try top-level message, but extract JSON if it's embedded
  if (error?.message) {
    // Check if message contains embedded JSON with detail
    const jsonMatch = error.message.match(/\{"detail":\{[^}]+\}\}/);

    if (jsonMatch) {
      try {
        const parsed = JSON.parse(jsonMatch[0]);

        if (parsed.detail?.message) {
          return parsed.detail.message;
        }
      } catch {
        // If parsing fails, fall through to return original message
      }
    }

    return error.message;
  }

  return undefined;
}

/**
 * Determines error code from status code or error type
 */
function determineErrorCode(error: any, status?: number): string {
  // Check for network errors
  if (error?.message?.includes("Network Error") || error?.message?.includes("Failed to fetch")) {
    return "NETWORK_ERROR";
  }

  // Check for timeout
  if (error?.message?.includes("timeout") || error?.code === "ECONNABORTED") {
    return "TIMEOUT";
  }

  // Check status codes
  if (status === 401 || status === 403) {
    return "UNAUTHORIZED";
  }

  if (status === 413) {
    return "FILE_TOO_LARGE";
  }

  if (status === 415) {
    return "UNSUPPORTED_TYPE";
  }

  return "UNKNOWN_ERROR";
}

/**
 * Parses a file upload error and returns structured error information
 *
 * @param error - The error object from the API call
 * @param fileName - The name of the file that failed to upload
 * @param fileSize - The size of the file in bytes
 * @returns Structured error information with user-friendly message and technical details
 */
export function parseFileUploadError(
  error: any,
  fileName: string,
  fileSize?: number,
): FileUploadError {
  // NEW: Check for structured backend error format (from quarantine/security failures)
  if (error?.structuredError?.error) {
    const structuredError = error.structuredError;
    const technicalDetails = structuredError.error.technical_details || "";

    // Try to extract specific error code from technical_details (e.g., MALWARE_DETECTED)
    const extractedCode = extractErrorCodeFromTechnicalDetails(technicalDetails);
    const errorCode =
      extractedCode || `${structuredError.error.category?.toUpperCase()}_ERROR` || "UNKNOWN_ERROR";

    // Use specific error mapping if we extracted a known code, otherwise use category-based suggestions
    const suggestions = extractedCode
      ? getErrorMapping(extractedCode).suggestions
      : getSuggestionsForCategory(structuredError.error.category);

    return {
      userMessage:
        structuredError.result ||
        structuredError.error.message ||
        i18next.t("fileUpload.errors.generic.message", { defaultValue: "File upload failed" }),
      technicalDetails: {
        errorCode,
        fileName,
        fileSize: fileSize?.toString(),
        originalMessage: technicalDetails || "No technical details",
        timestamp: new Date().toISOString(),
      },
      suggestions,
    };
  }

  // Fallback to original error parsing logic
  // Extract error code and message
  let errorCode = extractErrorCode(error) || determineErrorCode(error, error?.status);

  // Remap technical error codes to user-friendly categories
  if (errorCode === "ECONNABORTED") {
    errorCode = "TIMEOUT";
  }

  const originalMessage = extractErrorMessage(error);

  // Get user-friendly message and suggestions
  const errorMapping = getErrorMapping(errorCode);

  // For DUPLICATE_FILE errors, extract all file names from the error message
  let displayFileName = fileName;

  if (errorCode === "DUPLICATE_FILE" && originalMessage) {
    const match = originalMessage.match(/^DUPLICATE_FILE:\s*(.+)$/);

    if (match) {
      displayFileName = match[1]; // e.g., "file1.png, file2.png, file3.png"
    }
  }

  // Build technical details
  const technicalDetails = {
    errorCode,
    status: error?.status || error?.response?.status,
    fileName: displayFileName,
    fileSize: fileSize ? formatFileSize(fileSize) : undefined,
    originalMessage,
    timestamp: new Date().toISOString(),
  };

  return {
    userMessage: errorMapping.message,
    technicalDetails,
    suggestions: errorMapping.suggestions,
  };
}

/**
 * Formats a file upload error for console logging with full context
 *
 * @param error - The error object
 * @param fileName - The name of the file
 * @param fileSize - The size of the file in bytes
 * @param userId - Optional user ID for debugging
 * @param chatId - Optional chat ID for debugging
 * @returns Structured log object
 */
export function formatErrorForLogging(
  error: any,
  fileName: string,
  fileSize?: number,
  userId?: string,
  chatId?: string,
): Record<string, any> {
  const parsedError = parseFileUploadError(error, fileName, fileSize);

  return {
    event: "FILE_UPLOAD_ERROR",
    fileName,
    fileSize: fileSize ? formatFileSize(fileSize) : undefined,
    userId,
    chatId,
    errorCode: parsedError.technicalDetails.errorCode,
    status: parsedError.technicalDetails.status,
    message: parsedError.userMessage,
    originalError: parsedError.technicalDetails.originalMessage,
    timestamp: parsedError.technicalDetails.timestamp,
    // Include full error for debugging if needed
    rawError:
      process.env.NODE_ENV === "development"
        ? {
            message: error?.message,
            status: error?.status,
            detail: error?.detail,
          }
        : undefined,
  };
}
